import type {
  InvoiceMailLogEntry,
  InvoiceMailPreview,
  InvoiceMailPreviewInput,
  InvoiceMailState,
} from '@embertime/shared'
import { AlertTriangle, Check, ChevronDown, FileText, Loader2, Send } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { InvoiceDetail } from '../../lib/api'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import { Dialog } from '../ui/dialog'
import {
  BLOCKED_SENDER_TEXT,
  fileSize,
  formatLogTime,
  newAttemptId,
  nextTemplateId,
  pdfFormatLabel,
  recipientProblem,
  sendErrorText,
  templatesForSender,
} from './invoice-mail-logic'

// #536 „Rechnung senden“ (Mockup 536_mockup_pruefen_*): the server prepares
// the exact mail (sender, template, signature, PDF bytes) and binds it with a
// confirmation hash. „Jetzt senden“ is only enabled for an up-to-date
// preview; every edit triggers a fresh preview first. Nothing is sent or
// logged by opening, editing or cancelling.

const fieldLabel = 'mb-2 block text-sm font-semibold text-text-tertiary'
const fieldBox =
  'w-full rounded-xl border border-border-default bg-bg-base/40 px-4 py-3 text-base text-text-primary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20'

export interface InvoiceMailSheetBodyProps {
  state: InvoiceMailState
  identityId: string
  templateId: string | null
  recipient: string
  subject: string
  text: string
  preview: InvoiceMailPreview | null
  previous: InvoiceMailLogEntry | undefined
  recipientError: string | null
  error: string | null
  onIdentity?: (id: string) => void
  onTemplate?: (id: string | null) => void
  onRecipient?: (value: string) => void
  onSubject?: (value: string) => void
  onText?: (value: string) => void
  firstFieldRef?: React.Ref<HTMLSelectElement>
}

/** Presentational body, static-renderable for tests. */
export function InvoiceMailSheetBody({
  state,
  identityId,
  templateId,
  recipient,
  subject,
  text,
  preview,
  previous,
  recipientError,
  error,
  onIdentity,
  onTemplate,
  onRecipient,
  onSubject,
  onText,
  firstFieldRef,
}: InvoiceMailSheetBodyProps) {
  const sender = state.senders.find((s) => s.identityId === identityId)
  const allowedTemplates = templatesForSender(state.templates, sender)
  const pdf = preview?.pdf ?? { ...state.pdf, sha256: '' }
  return (
    <div className="space-y-5 pb-3 pt-1">
      {previous && (
        <div
          role="note"
          className="rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm leading-relaxed"
        >
          <strong className="block">Bereits versendet</strong>
          <span className="text-text-secondary">
            {formatLogTime(previous.acceptedAt ?? previous.reservedAt)} an {previous.recipient}.
            Erneutes Senden erzeugt eine neue Mail und einen eigenen Protokolleintrag.
          </span>
        </div>
      )}

      <div>
        <label htmlFor="invoice-mail-from" className={fieldLabel}>
          Von · Firma und Identität
        </label>
        <div className="relative rounded-xl focus-within:ring-2 focus-within:ring-accent/40">
          <div
            aria-hidden="true"
            className={cn(fieldBox, 'pointer-events-none flex items-center justify-between gap-3')}
          >
            <span className="min-w-0">
              <strong className="block truncate text-base">
                {sender?.companyName ?? 'Keine Firma'}
              </strong>
              <span className="block truncate text-sm text-text-tertiary">
                {sender?.address ?? 'Kein Absender'}
              </span>
            </span>
            <ChevronDown className="h-5 w-5 shrink-0 text-text-tertiary" />
          </div>
          <select
            id="invoice-mail-from"
            ref={firstFieldRef}
            value={identityId}
            onChange={(event) => onIdentity?.(event.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          >
            {state.senders.map((s) => (
              <option key={s.identityId} value={s.identityId} disabled={s.blockedCode !== null}>
                {s.companyName ?? 'Keine Firma'} · {s.address}
                {s.blockedCode ? ` (gesperrt: ${BLOCKED_SENDER_TEXT[s.blockedCode]})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="invoice-mail-to" className={fieldLabel}>
          An · aus Kundenstamm
        </label>
        <input
          id="invoice-mail-to"
          type="email"
          inputMode="email"
          autoComplete="off"
          maxLength={254}
          value={recipient}
          onChange={(event) => onRecipient?.(event.target.value)}
          aria-invalid={recipientError ? true : undefined}
          aria-describedby={recipientError ? 'invoice-mail-to-error' : undefined}
          className={cn(fieldBox, recipientError && 'border-danger/60')}
        />
        {recipientError && (
          <p id="invoice-mail-to-error" className="mt-1.5 text-sm text-danger">
            {recipientError}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="invoice-mail-template" className={fieldLabel}>
          Vorlage
        </label>
        <div className="relative">
          <select
            id="invoice-mail-template"
            value={templateId ?? ''}
            onChange={(event) => onTemplate?.(event.target.value || null)}
            className={cn(fieldBox, 'appearance-none pr-11')}
          >
            {allowedTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
            <option value="">Ohne Vorlage</option>
          </select>
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary"
          />
        </div>
      </div>

      <div>
        <label htmlFor="invoice-mail-subject" className={fieldLabel}>
          Betreff
        </label>
        <input
          id="invoice-mail-subject"
          type="text"
          maxLength={300}
          value={subject}
          onChange={(event) => onSubject?.(event.target.value)}
          className={fieldBox}
        />
      </div>

      <div>
        <label htmlFor="invoice-mail-text" className={fieldLabel}>
          Nachricht
        </label>
        <textarea
          id="invoice-mail-text"
          rows={4}
          maxLength={20000}
          value={text}
          onChange={(event) => onText?.(event.target.value)}
          className={cn(fieldBox, 'min-h-28 resize-y leading-relaxed')}
        />
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-border-default bg-bg-base/40 px-4 py-3">
        <FileText aria-hidden="true" className="h-6 w-6 shrink-0 text-accent" />
        <span className="min-w-0 flex-1">
          <strong className="block truncate text-base">{pdf.name}</strong>
          <span className="block text-sm text-text-tertiary">
            {fileSize(pdf.size)} · {pdfFormatLabel(pdf.format)} · identisch mit PDF-Download
          </span>
        </span>
        {preview && <Check aria-label="Anhang geprüft" className="h-5 w-5 shrink-0 text-accent" />}
      </div>

      {preview && (
        <div className="border-l-2 border-accent pl-4 text-sm text-text-tertiary">
          {preview.signature.attached ? (
            <>
              <p className="whitespace-pre-line leading-relaxed">{preview.signature.text}</p>
              <p className="mt-1">Firmensignatur automatisch angehängt</p>
            </>
          ) : (
            <p>Ohne Signatur – die Vorlage hängt keine Firmensignatur an.</p>
          )}
          {preview.signature.warning && (
            <p className="mt-1 flex items-center gap-1.5 text-warning">
              <AlertTriangle aria-hidden="true" className="h-4 w-4" />
              {preview.signature.warningLabel ?? 'Pflichtangabe fehlt'} – Versand bleibt möglich
            </p>
          )}
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export function InvoiceMailSheet({
  invoice,
  state,
  open,
  previous,
  onClose,
  onSent,
}: {
  invoice: InvoiceDetail
  state: InvoiceMailState
  open: boolean
  previous: InvoiceMailLogEntry | undefined
  onClose: () => void
  onSent: (entry: InvoiceMailLogEntry) => void
}) {
  const [identityId, setIdentityId] = useState(state.defaultIdentityId ?? '')
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [recipient, setRecipient] = useState(state.recipient ?? '')
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [preview, setPreview] = useState<InvoiceMailPreview | null>(null)
  const [dirty, setDirty] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const attempt = useRef<{ confirmation: string; id: string } | null>(null)
  const requestSeq = useRef(0)
  const firstField = useRef<HTMLSelectElement>(null)

  const recipientError =
    recipient.trim() || (preview && !loading) ? recipientProblem(recipient) : null

  const runPreview = useCallback(
    async (input: InvoiceMailPreviewInput) => {
      const seq = ++requestSeq.current
      setLoading(true)
      try {
        const { preview: next } = await api.previewInvoiceMail(invoice.id, input)
        if (seq !== requestSeq.current) return
        setPreview(next)
        setTemplateId(next.templateId)
        setSubject(next.subject)
        setText(next.text)
        setRecipient((current) => (input.recipient === undefined ? next.recipient : current))
        setDirty(false)
        setError(null)
      } catch (err) {
        if (seq !== requestSeq.current) return
        const e = err as Error & { code?: string }
        setPreview(null)
        setError(sendErrorText(e.code, e.message))
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    },
    [invoice.id],
  )

  // Open: fresh sheet from the server's defaults (template text, customer
  // address). Runs once per opening; a background refetch of the state must
  // not reset what the user is checking.
  const stateRef = useRef(state)
  stateRef.current = state
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per opening
  useEffect(() => {
    if (!open) return
    const current = stateRef.current
    const sender = current.senders.find((s) => s.identityId === current.defaultIdentityId)
    const firstTemplate = nextTemplateId(null, templatesForSender(current.templates, sender))
    setIdentityId(current.defaultIdentityId ?? '')
    setRecipient(current.recipient ?? '')
    setTemplateId(firstTemplate)
    setPreview(null)
    setDirty(false)
    setError(null)
    attempt.current = null
    if (current.defaultIdentityId)
      void runPreview({
        identityId: current.defaultIdentityId,
        templateId: firstTemplate,
        applyTemplate: true,
      })
    else setError('Kein Absender mit Firma und Journal-Archiv verfügbar.')
    const timer = window.setTimeout(() => firstField.current?.focus(), 50)
    return () => window.clearTimeout(timer)
  }, [open])

  // Edits of recipient, subject or text: fresh binding preview after a pause.
  useEffect(() => {
    if (!open || !dirty || !identityId) return
    if (recipient.trim() && recipientProblem(recipient)) return
    const timer = window.setTimeout(() => {
      void runPreview({
        identityId,
        templateId,
        applyTemplate: false,
        recipient: recipient.trim(),
        subject,
        text,
      })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [open, dirty, identityId, templateId, recipient, subject, text, runPreview])

  const changeIdentity = (id: string) => {
    const sender = state.senders.find((s) => s.identityId === id)
    const nextTemplate = nextTemplateId(templateId, templatesForSender(state.templates, sender))
    setIdentityId(id)
    setTemplateId(nextTemplate)
    setPreview(null)
    void runPreview({
      identityId: id,
      templateId: nextTemplate,
      applyTemplate: true,
      recipient: recipient.trim() && !recipientError ? recipient.trim() : undefined,
    })
  }
  const changeTemplate = (id: string | null) => {
    setTemplateId(id)
    setPreview(null)
    void runPreview({
      identityId,
      templateId: id,
      applyTemplate: true,
      recipient: recipient.trim() && !recipientError ? recipient.trim() : undefined,
    })
  }
  const edit = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    setDirty(true)
  }

  const ready =
    !!preview?.confirmation && !dirty && !loading && !sending && !recipientError && !!identityId

  const send = async () => {
    if (!preview?.confirmation || !ready) return
    // One attempt per confirmed content: a retry after a lost answer reuses it
    // and the server answers from its log instead of sending again.
    if (attempt.current?.confirmation !== preview.confirmation)
      attempt.current = { confirmation: preview.confirmation, id: newAttemptId() }
    setSending(true)
    setError(null)
    try {
      const result = await api.sendInvoiceMail(invoice.id, {
        attemptId: attempt.current.id,
        identityId: preview.identityId,
        templateId: preview.templateId,
        recipient: preview.recipient,
        subject: preview.subject,
        text: preview.text,
        format: preview.pdf.format,
        confirmation: preview.confirmation,
        resend: preview.previousSends > 0,
      })
      onSent(result.entry)
    } catch (err) {
      const e = err as Error & { code?: string }
      setError(sendErrorText(e.code, e.message))
      if (e.code === 'INVOICE_MAIL_STALE' || e.code === 'INVOICE_MAIL_RESEND_REQUIRED')
        void runPreview({
          identityId,
          templateId,
          applyTemplate: false,
          recipient: recipient.trim(),
          subject,
          text,
        })
    } finally {
      setSending(false)
    }
  }

  const resend = (preview?.previousSends ?? (previous ? 1 : 0)) > 0
  const footer = (
    <div className="grid w-full grid-cols-2 gap-3">
      <button
        type="button"
        onClick={onClose}
        className="min-h-12 rounded-xl border border-border-default bg-bg-base/40 text-base font-semibold hover:bg-bg-overlay"
      >
        Abbrechen
      </button>
      <button
        type="button"
        onClick={() => void send()}
        disabled={!ready}
        className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-accent text-base font-semibold text-bg-base hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {sending || loading ? (
          <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
        ) : (
          <Send aria-hidden="true" className="h-5 w-5" />
        )}
        {resend ? 'Erneut senden' : 'Jetzt senden'}
      </button>
    </div>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Rechnung senden"
      description={`${invoice.invoiceNumber} · Versand vorab prüfen`}
      footer={footer}
      className="sm:max-w-xl"
    >
      <InvoiceMailSheetBody
        state={state}
        identityId={identityId}
        templateId={templateId}
        recipient={recipient}
        subject={subject}
        text={text}
        preview={preview}
        previous={previous}
        recipientError={recipientError}
        error={error}
        onIdentity={changeIdentity}
        onTemplate={changeTemplate}
        onRecipient={edit(setRecipient)}
        onSubject={edit(setSubject)}
        onText={edit(setText)}
        firstFieldRef={firstField}
      />
    </Dialog>
  )
}
